import React, { useState, useEffect, useCallback } from 'react';
import './SupportTickets.css';
import { API_BASE_URL, debugLog, ENABLE_SMART_ROUTER } from './config';
import { smartAPI } from './smartAPI';
import { useTranslation } from './i18n';

// Helper: Smart fetch with automatic failover
const apiFetch = async (path, options = {}) => {
  if (ENABLE_SMART_ROUTER) {
    return smartAPI.request(path, options);
  } else {
    return fetch(`${API_BASE_URL}${path}`, options);
  }
};

// Status colors and icons (labels will come from i18n)
const STATUS_CONFIG = {
  new: { color: '#3498db', icon: '🆕' },
  open: { color: '#2ecc71', icon: '📬' },
  pending: { color: '#f39c12', icon: '⏳' },
  resolved: { color: '#27ae60', icon: '✅' },
  closed: { color: '#95a5a6', icon: '🔒' }
};

// Default categories keys (will be translated in component)
const CATEGORY_KEYS = [
  { id: 1, key: 'technical', icon: '🔧' },
  { id: 2, key: 'billing', icon: '💳' },
  { id: 3, key: 'general', icon: '❓' },
  { id: 4, key: 'feedback', icon: '💡' }
];

function SupportTickets({ authData, initData, onClose }) {
  const { t, language } = useTranslation();

  // Helper: Get status label from i18n
  const getStatusLabel = (status) => t(`support.tickets.status.${status}`);

  // Helper: Get localized default categories
  const getDefaultCategories = useCallback(() => CATEGORY_KEYS.map(cat => ({
    id: cat.id,
    name: t(`categories.${cat.key}`),
    icon: cat.icon
  })), [t]);

  const [view, setView] = useState('list'); // 'list', 'create', 'detail'
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create ticket form state
  const [newTicket, setNewTicket] = useState({
    category_id: '',
    description: ''
  });
  const [createLoading, setCreateLoading] = useState(false);

  // Reply form state
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  // Build auth headers for API requests
  const getAuthHeaders = useCallback(() => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Use initData prop first, fallback to window.Telegram
    const telegramInitData = initData || window.Telegram?.WebApp?.initData;
    debugLog('getAuthHeaders - initData:', telegramInitData ? `length=${telegramInitData.length}` : 'EMPTY');
    
    if (telegramInitData) {
      headers['X-Telegram-Auth'] = telegramInitData;
    }
    
    return headers;
  }, [initData]);

  // Load tickets
  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/support/tickets', {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTickets(data.tickets || []);
      } else {
        setError(data.error || t('errors.loadTickets'));
      }
    } catch (err) {
      debugLog('Load tickets error:', err);
      setError(t('errors.connection'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const response = await apiFetch('/api/support/categories', {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      debugLog('Categories response:', data);
      
      if (data.success && data.categories && data.categories.length > 0) {
        setCategories(data.categories);
      } else {
        // Use fallback categories if API returns empty
        debugLog('Using fallback categories');
        setCategories(getDefaultCategories());
      }
    } catch (err) {
      debugLog('Load categories error:', err);
      // Use fallback on error
      setCategories(getDefaultCategories());
    }
  }, [getAuthHeaders, getDefaultCategories]);

  // Load ticket details
  const loadTicketDetails = useCallback(async (ticketNumber) => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/support/tickets/${ticketNumber}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedTicket(data.ticket);
        setView('detail');
      } else {
        setError(data.error || t('errors.loadTicket'));
      }
    } catch (err) {
      debugLog('Load ticket details error:', err);
      setError(t('errors.connection'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  // Create ticket
  const handleCreateTicket = async () => {
    if (!newTicket.category_id || !newTicket.description.trim()) {
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(t('validation.fillAllFields'));
      }
      return;
    }
    
    if (newTicket.description.length < 10) {
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(t('validation.descriptionTooShort'));
      }
      return;
    }
    
    try {
      setCreateLoading(true);
      
      const response = await apiFetch('/api/support/tickets', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          category_id: parseInt(newTicket.category_id),
          description: newTicket.description.trim(),
          auth_data: {
            initData: window.Telegram?.WebApp?.initData,
            ...authData
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Success - show popup and go back to list
        if (window.Telegram?.WebApp?.showPopup) {
          window.Telegram.WebApp.showPopup({
            title: `✅ ${t('tickets.created.title')}`,
            message: t('tickets.created.message', { number: data.ticket_number }),
            buttons: [{ id: 'ok', type: 'default', text: 'OK' }]
          });
        }
        
        // Reset form and refresh list
        setNewTicket({ category_id: '', description: '' });
        setView('list');
        loadTickets();
      } else {
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert(data.error || t('validation.createTicketFailed'));
        }
      }
    } catch (err) {
      debugLog('Create ticket error:', err);
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(t('errors.connection'));
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // Reply to ticket
  const handleReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) {
      return;
    }
    
    if (replyMessage.trim().length < 5) {
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(t('validation.messageTooShort'));
      }
      return;
    }
    
    try {
      setReplyLoading(true);
      
      const response = await apiFetch(`/api/support/tickets/${selectedTicket.ticket_number}/reply`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          message: replyMessage.trim(),
          auth_data: {
            initData: window.Telegram?.WebApp?.initData,
            ...authData
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setReplyMessage('');
        // Reload ticket to see new message
        loadTicketDetails(selectedTicket.ticket_number);
        
        // Haptic feedback
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
        }
      } else {
        if (window.Telegram?.WebApp?.showAlert) {
          window.Telegram.WebApp.showAlert(data.error || t('validation.replyFailed'));
        }
      }
    } catch (err) {
      debugLog('Reply error:', err);
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(t('errors.connection'));
      }
    } finally {
      setReplyLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadTickets();
    loadCategories();
  }, [loadTickets, loadCategories]);

  // Format date with localization
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const locale = language === 'ru' ? 'ru-RU' : 'en-US';
    
    // Less than 1 minute
    if (diff < 60000) {
      return t('time.justNow');
    }
    
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return t('time.minutesAgo', { count: mins });
    }
    
    // Today - show time
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      return t('time.yesterday', { time });
    }
    
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return t('time.daysAgo', { count: days });
    }
    
    // Full date
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  // Render ticket list
  const renderList = () => (
    <div className="tickets-list">
      <div className="tickets-header">
        <h2>📋 {t('support.tickets.title')}</h2>
        <button 
          className="create-ticket-btn"
          onClick={() => setView('create')}
        >
          ✏️ {t('support.tickets.createNew')}
        </button>
      </div>
      
      {loading ? (
        <div className="tickets-loading">
          <div className="spinner"></div>
          <p>{t('support.tickets.loading')}</p>
        </div>
      ) : error ? (
        <div className="tickets-error">
          <p>{error}</p>
          <button onClick={loadTickets}>{t('common.retry')}</button>
        </div>
      ) : tickets.length === 0 ? (
        <div className="tickets-empty">
          <div className="empty-icon">📭</div>
          <p>{t('support.tickets.noTickets')}</p>
          <button 
            className="create-first-ticket"
            onClick={() => setView('create')}
          >
            {t('support.tickets.createNew')}
          </button>
        </div>
      ) : (
        <div className="tickets-items">
          {tickets.map(ticket => (
            <div 
              key={ticket.id}
              className={`ticket-card ${ticket.has_unread_replies ? 'has-unread' : ''}`}
              onClick={() => loadTicketDetails(ticket.ticket_number)}
            >
              <div className="ticket-header">
                <span className="ticket-number">#{ticket.ticket_number}</span>
                <span 
                  className="ticket-status"
                  style={{ backgroundColor: STATUS_CONFIG[ticket.status]?.color }}
                >
                  {STATUS_CONFIG[ticket.status]?.icon} {getStatusLabel(ticket.status)}
                </span>
              </div>
              
              <div className="ticket-subject">{ticket.subject}</div>
              
              <div className="ticket-meta">
                <span className="ticket-category">
                  {ticket.category?.icon} {ticket.category?.name}
                </span>
                <span className="ticket-date">{formatDate(ticket.created_at)}</span>
              </div>
              
              {ticket.has_unread_replies && (
                <div className="unread-badge">
                  {ticket.unread_count} new
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render create form
  const renderCreate = () => (
    <div className="create-ticket">
      <div className="create-header">
        <button className="back-btn" onClick={() => setView('list')}>
          ← {t('common.back')}
        </button>
        <h2>✏️ {t('support.tickets.createNew')}</h2>
      </div>
      
      <div className="create-form">
        <div className="form-group">
          <label>{t('support.tickets.form.category')}</label>
          <div className="category-grid">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-btn ${newTicket.category_id === cat.id ? 'selected' : ''}`}
                onClick={() => setNewTicket(prev => ({ ...prev, category_id: cat.id }))}
              >
                <span className="cat-icon">{cat.icon}</span>
                <span className="cat-name">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="form-group">
          <label>{t('support.tickets.form.description')}</label>
          <textarea
            value={newTicket.description}
            onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
            placeholder={t('support.tickets.form.descriptionPlaceholder')}
            rows={5}
            maxLength={2000}
          />
          <div className="char-count">
            {newTicket.description.length}/2000
          </div>
        </div>
        
        <button 
          className="submit-btn"
          onClick={handleCreateTicket}
          disabled={createLoading || !newTicket.category_id || newTicket.description.length < 10}
        >
          {createLoading ? `⏳ ${t('support.tickets.form.sending')}` : `📤 ${t('support.tickets.form.submit')}`}
        </button>
      </div>
    </div>
  );

  // Render ticket detail
  const renderDetail = () => {
    if (!selectedTicket) return null;
    
    const status = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.new;
    
    return (
      <div className="ticket-detail">
        <div className="detail-header">
          <button className="back-btn" onClick={() => { setSelectedTicket(null); setView('list'); }}>
            ← {t('common.back')}
          </button>
          <div className="detail-title">
            <span className="detail-number">#{selectedTicket.ticket_number}</span>
            <span 
              className="detail-status"
              style={{ backgroundColor: status.color }}
            >
              {status.icon} {getStatusLabel(selectedTicket.status)}
            </span>
          </div>
        </div>
        
        <div className="detail-info">
          <h3>{selectedTicket.subject}</h3>
          <div className="detail-meta">
            <span>{selectedTicket.category?.icon} {selectedTicket.category?.name}</span>
            <span>{t('support.tickets.detail.created')}: {formatDate(selectedTicket.created_at)}</span>
          </div>
        </div>
        
        <div className="messages-container">
          {/* Original description as first message */}
          <div className="message user-message">
            <div className="message-content">{selectedTicket.description}</div>
            <div className="message-time">{formatDate(selectedTicket.created_at)}</div>
          </div>
          
          {/* Conversation messages */}
          {selectedTicket.messages?.map(msg => (
            <div 
              key={msg.id}
              className={`message ${msg.is_admin ? 'admin-message' : 'user-message'}`}
            >
              {msg.is_admin && <div className="message-author">👨‍💼 {t('support.tickets.detail.adminReply')}</div>}
              <div className="message-content">{msg.message}</div>
              <div className="message-time">{formatDate(msg.created_at)}</div>
            </div>
          ))}
        </div>
        
        {/* Reply form (if ticket is not closed) */}
        {selectedTicket.status !== 'closed' && (
          <div className="reply-form">
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder={t('support.tickets.detail.replyPlaceholder')}
              rows={3}
            />
            <button 
              className="reply-btn"
              onClick={handleReply}
              disabled={replyLoading || replyMessage.length < 5}
            >
              {replyLoading ? '⏳' : '📤'} {t('support.tickets.detail.reply')}
            </button>
          </div>
        )}
        
        {selectedTicket.status === 'closed' && (
          <div className="ticket-closed-notice">
            🔒 {getStatusLabel('closed')}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="support-tickets">
      <div className="tickets-close" onClick={onClose}>×</div>
      
      {view === 'list' && renderList()}
      {view === 'create' && renderCreate()}
      {view === 'detail' && renderDetail()}
    </div>
  );
}

export default SupportTickets;